import type { LucideIcon } from "lucide-react";
import type { KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

export interface OpcionSegmented<T extends string> {
  valor: T;
  etiqueta: string;
  contador?: number;
  icono?: LucideIcon;
  /**
   * Un color para dibujar antes del texto (el color de un iPhone). Es un
   * cuadradito y no un fondo: el chip elegido sigue marcándose igual que todos.
   */
  muestra?: string;
  /** Tinte del contador cuando > 0 (ChipsEstado: negativos en destructive, bajos en warning). */
  tono?: "warning" | "destructive";
  /** Esta opcion sola no se puede elegir (TablaRevision: "Actualizar" sin producto). */
  disabled?: boolean;
  /** Motivo de la deshabilitacion, como tooltip nativo. */
  title?: string;
  /**
   * Nombre accesible del boton cuando el texto visible no alcanza: con contador
   * el lector lee "Negativo2"; ChipsEstado prefiere "2 Negativo", que es como se
   * dice en voz alta.
   */
  "aria-label"?: string;
}

export interface SegmentedProps<T extends string> {
  opciones: ReadonlyArray<OpcionSegmented<T>>;
  /**
   * `null`: ninguna elegida todavia. Para la decision que no tiene que tomarse
   * sola (el anclaje de la cuenta de un cliente nuevo).
   */
  valor: T | null;
  onChange: (v: T) => void;
  /** Etiqueta accesible del grupo (no hay label visible). */
  etiqueta: string;
  /**
   * `segmentado`: barra unida (formularios, orden de una lista).
   * `chips`: botones sueltos (filtros de pagina), con el mismo radio que el
   * resto de los controles.
   */
  apariencia?: "segmentado" | "chips";
  /** `sm` 36/32 · `md` 44/40. Default: segmentado `sm`, chips `md`. */
  tamano?: "sm" | "md";
  disabled?: boolean;
  className?: string;
}

const CONTENEDOR = {
  segmentado: "inline-flex gap-0.5 rounded-md border border-input bg-muted p-0.5",
  chips: "flex flex-wrap gap-2",
} as const;

const ITEM_BASE =
  "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0";

// En segmentado el alto del item es el del control menos el borde y el p-0.5
// del contenedor, para que el conjunto de 36/32 y 44/40 como todo control.
const ITEM = {
  segmentado: {
    // 36 en el celular (antes 32): ver el `sm` de Button.
    sm: "h-9 rounded-sm px-3 text-xs sm:h-7 [&_svg]:size-3.5",
    md: "h-10 rounded-sm px-3 text-sm sm:h-9 [&_svg]:size-4",
  },
  chips: {
    sm: "h-10 rounded-md border border-input px-3 text-xs sm:h-8 [&_svg]:size-3.5",
    md: "h-11 rounded-md border border-input px-3 text-sm sm:h-10 [&_svg]:size-4",
  },
} as const;

// Un solo "activo": negro solido. `bg-accent` y `bg-secondary` en light son casi
// el fondo y el activo no se distinguia del hover.
const ACTIVO = "border-primary bg-primary text-primary-foreground";
const INACTIVO = "text-muted-foreground hover:bg-accent hover:text-foreground";

const CONTADOR_TONO = {
  warning: "font-semibold text-warning",
  destructive: "font-semibold text-destructive",
} as const;

const TECLAS_ATRAS = new Set(["ArrowLeft", "ArrowUp"]);
const TECLAS_ADELANTE = new Set(["ArrowRight", "ArrowDown"]);

/**
 * Grupo de opciones excluyentes, controlado. Botones con `aria-pressed` y no un
 * `<select>` ni radios: el estado se ve siempre, sin abrir nada, y se lee sin
 * color (el activo tiene fondo solido). Las flechas mueven la seleccion y el
 * foco; Home y End van a los extremos. `SelectorMoneda` sigue aparte porque es
 * el unico segmented donde el color del activo es informacion.
 */
export function Segmented<T extends string>({
  opciones,
  valor,
  onChange,
  etiqueta,
  apariencia = "segmentado",
  tamano = apariencia === "segmentado" ? "sm" : "md",
  disabled = false,
  className,
}: SegmentedProps<T>): JSX.Element {
  function alTeclado(e: KeyboardEvent<HTMLDivElement>): void {
    const cantidad = opciones.length;
    if (cantidad === 0) return;
    const actual = Math.max(
      opciones.findIndex((o) => o.valor === valor),
      0,
    );

    let destino: number;
    if (TECLAS_ATRAS.has(e.key)) destino = (actual - 1 + cantidad) % cantidad;
    else if (TECLAS_ADELANTE.has(e.key)) destino = (actual + 1) % cantidad;
    else if (e.key === "Home") destino = 0;
    else if (e.key === "End") destino = cantidad - 1;
    else return;

    e.preventDefault();
    const opcion = opciones[destino];
    // Una opcion deshabilitada no se elige ni con el teclado: se la saltea.
    if (!opcion || opcion.disabled) return;
    onChange(opcion.valor);
    // El foco sigue a la seleccion; los botones se buscan en el DOM y no por
    // refs para que el componente siga siendo generico sin un array de refs.
    e.currentTarget.querySelectorAll<HTMLButtonElement>("button[data-valor]")[destino]?.focus();
  }

  return (
    <div
      role="group"
      aria-label={etiqueta}
      className={cn(CONTENEDOR[apariencia], className)}
      onKeyDown={alTeclado}
    >
      {opciones.map((o) => {
        const activo = o.valor === valor;
        const Icono = o.icono;
        return (
          <button
            key={o.valor}
            type="button"
            data-valor={o.valor}
            aria-label={o["aria-label"]}
            aria-pressed={activo}
            disabled={disabled || o.disabled}
            title={o.title}
            onClick={() => onChange(o.valor)}
            className={cn(ITEM_BASE, ITEM[apariencia][tamano], activo ? ACTIVO : INACTIVO)}
          >
            {Icono ? <Icono aria-hidden="true" /> : null}
            {o.muestra ? (
              <span
                aria-hidden="true"
                className="size-4 shrink-0 rounded-full border border-border"
                style={{ backgroundColor: o.muestra }}
              />
            ) : null}
            {o.etiqueta}
            {o.contador !== undefined ? (
              <span
                className={cn(
                  "rounded-md px-1.5 text-xs tabular-nums",
                  activo
                    ? "bg-primary-foreground/15 text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                  !activo && o.tono && o.contador > 0 && CONTADOR_TONO[o.tono],
                )}
              >
                {o.contador}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
